import React, { useEffect, useState } from 'react';
import { updateOrCreate } from '../../../special/updateOrCreate';
import { Box, Button, IconButton, Typography, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { NewCalendar } from './NewCalendar';
import AddBoxIcon from '@mui/icons-material/AddBox';
import { ExoVariants } from '../../ExoVariants';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import { NewProviderCalendar } from './NewProviderCalendar';
import ExoDialog from '../../ExoDialog';
import { ProjectAddElementButton } from '../../project/ProjectAddElementButton';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import { SettingsPopover } from '../../project/SettingsPopover';
import { tokens } from '../../../../global/theme/tokens';
import { ShareCalendar } from './ShareCalendar';
import Cookies from 'universal-cookie';
import { CALENDAR_PROVIDERS } from '../../../../config/calendarProviders';

export const ExoCalList = ({ calendars, onChange, onDelete }) => {
  const { t } = useTranslation();

  const cookies = new Cookies();

  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(null);
  const [share, setShare] = useState(null);

  function handleClick(cal) {
    const newCal = { ...cal, isInactive: !cal.isInactive };
    handleUpdate(newCal);
  }

  function handleUpdate(cal) {
    setOpen(false);
    setEdit(null);
    setShare(null);
    const newCals = updateOrCreate(calendars, cal);
    handleCookieUpdate(newCals);
    onChange(newCals);
  }

  function handleCookieUpdate(cals) {
    const current = new Date();
    const nextYear = new Date();
    nextYear.setFullYear(current.getFullYear() + 1);

    let hiddenCals = [];
    cals.forEach((element) => {
      if (element.isInactive) if (element.id) hiddenCals.push(element.id);
    });

    cookies.set('hiddenCals', JSON.stringify(hiddenCals), {
      path: '/',
      expires: nextYear,
    });
  }

  const calendarServicesEnabled = cookies.get('calendarServicesEnabled') || {};

  const variants = [
    {
      label: t('Exo Calendar'),
      Icon: AddCircleOutlineIcon,
      content: (
        <NewCalendar onClose={() => setOpen(false)} onCreate={handleUpdate} />
      ),
    },
    ...Object.values(CALENDAR_PROVIDERS).map((provider) => {
      const isEnabled = calendarServicesEnabled[provider.type] !== false;
      return {
        label: t(provider.name),
        icon: provider.logoShort || provider.logo,
        content: <NewProviderCalendar provider={provider.type} />,
        disabled: !isEnabled,
        disabledMessage: `${provider.name} has not been configured. Please contact support in order to use ${provider.name}.`,
      };
    }),
  ];

  return (
    <Box
      className="flex flex-col gap-2 p-2"
      sx={{
        width: '500px',
        maxWidth: '100%',
      }}
    >
      <Typography variant="h3" fontWeight={500}>
        <CalendarMonthIcon className="mr-2" />
        {t('Calendars')}
      </Typography>
      {calendars?.map((cal) => (
        <Calendar
          key={cal.id}
          cal={cal}
          onSelect={handleClick}
          onEdit={setEdit}
          onDelete={() => onDelete(cal, calendars)}
          onShare={setShare}
          showSettings
        />
      ))}
      <ProjectAddElementButton
        label={t('Add Calendar')}
        size="small"
        onClick={() => setOpen(true)}
      />
      <ExoDialog open={open} onClose={() => setOpen(false)} limitWidth>
        <ExoVariants variants={variants} />
      </ExoDialog>
      <ExoDialog open={edit} onClose={() => setEdit(null)} limitWidth>
        <NewCalendar
          onClose={() => setEdit(null)}
          startData={edit}
          onCreate={handleUpdate}
        />
      </ExoDialog>
      <ExoDialog open={share} onClose={() => setShare(null)} limitWidth>
        <ShareCalendar
          calendar={share}
          onChange={handleUpdate}
          onClose={() => {
            setShare(null);
          }}
        />
      </ExoDialog>
    </Box>
  );
};

export const Calendar = ({
  cal,
  onSelect,
  noIcon,
  onEdit,
  onDelete,
  onShare,
  showSettings,
  size = 'small',
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);

  const { color, name, id, isInactive } = cal;

  const Container = onSelect ? Button : Box;

  const settings = [
    {
      label: t('Edit'),
      color: 'info',
      onClick: () => onEdit(cal),
    },
    {
      label: t('Share'),
      color: 'info',
      onClick: () => onShare(cal),
    },
    {
      label: t('Delete'),
      color: 'error',
      onClick: () => onDelete(cal),
    },
  ];

  const isSmall = size === 'small';

  return (
    <Box
      className="flex gap-2 w-full rounded-md"
      sx={{
        border: `1px solid transparent`,
        ':hover': {
          bgcolor: onSelect && colors.info + '20',
          border: onSelect && `1px solid ${colors.info}80`,
        },
      }}
    >
      <Container
        onClick={onSelect ? () => onSelect(cal) : null}
        sx={{
          opacity: isInactive ? 0.3 : 1,
        }}
        className=" whitespace-nowrap w-full"
        size="small"
      >
        <Typography
          textAlign="left"
          className="w-full h-full flex items-center px-2 rounded-md"
          fontWeight={500}
          sx={{
            fontSize: isSmall ? '12px' : '17px',
            bgcolor: color + '30',
          }}
        >
          {!noIcon && (
            <>
              {!isInactive ? (
                <VisibilityIcon sx={{ color, fontSize: 15 }} />
              ) : (
                <VisibilityOffIcon sx={{ color, fontSize: 15 }} />
              )}
            </>
          )}
          <span
            style={{
              backgroundColor: color,
              width: '5px',
              height: '70%',
              borderRadius: '2px',
              marginRight: '5px',
              marginLeft: noIcon ? '0px' : '10px',
            }}
          />

          {name}
        </Typography>
      </Container>
      {showSettings && cal.id && (
        <SettingsPopover
          settings={
            cal.writePermissions
              ? settings
              : [
                  {
                    label: t('Delete'),
                    color: 'error',
                    onClick: () => onDelete(cal),
                  },
                ]
          }
        />
      )}
    </Box>
  );
};
